import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Vehicles from './pages/Vehicles';
import Contracts from './pages/Contracts';
import Payments from './pages/Payments';
import Creditors from './pages/Creditors';
import Alerts from './pages/Alerts';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Expenses from './pages/Expenses';
import Profitability from './pages/Profitability';
import Documents from './pages/Documents';
import './index.css';

// Rutas permitidas por rol (además de las rutas comunes /settings admin-only, etc.).
// Si un rol no aparece aquí, tiene acceso completo a lo permitido por la UI general.
const ROLE_ALLOWED_ROUTES = {
  contabilidad: ['/payments', '/expenses'],
  cobranza: ['/payments', '/alerts'],
  egresos: ['/expenses', '/documents'],
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, defaultRoute } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const allowed = ROLE_ALLOWED_ROUTES[user.role];
  if (allowed && location.pathname !== '/' && !allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return <Navigate to={defaultRoute || '/dashboard'} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, defaultRoute } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={defaultRoute || '/dashboard'} replace />;
  }

  return children;
};

const DefaultRedirect = () => {
  const { defaultRoute } = useAuth();
  return <Navigate to={defaultRoute || '/dashboard'} replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DefaultRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="vehicles" element={<Vehicles />} />
        <Route path="contracts" element={<Contracts />} />
        <Route path="payments" element={<Payments />} />
        <Route path="creditors" element={<Creditors />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="profitability" element={<Profitability />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="reports" element={<Reports />} />
        <Route path="documents" element={<Documents />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
